import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircle,
  GraphicEq,
  PlaylistAddCheck,
  Refresh,
  Replay,
  SkipNext,
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;
const ACCENT = '#22d3ee';
const REFRESH_MS = 30000;

const STATUS_META = {
  awaiting_approval: { label: 'Awaiting approval', color: '#ffb74d' },
  pending: { label: 'Queued', color: '#90a4ae' },
  generating: { label: 'Generating', color: ACCENT },
  completed: { label: 'Ready to air', color: '#4caf50' },
  failed: { label: 'Failed', color: '#ef5350' },
  skipped: { label: 'Skipped', color: '#90a4ae' },
  superseded: { label: 'Regenerated', color: '#90a4ae' },
  expired: { label: 'Expired (not approved in time)', color: '#ef5350' },
  cancelled: { label: 'Cancelled', color: '#90a4ae' },
};

const cardSx = {
  bgcolor: 'var(--rs-paper)',
  border: '1px solid rgba(148,163,184,0.14)',
  borderRadius: 3,
  overflow: 'hidden',
};

const jobPaperSx = {
  p: 2,
  bgcolor: 'var(--rs-overlay-soft)',
  border: '1px solid rgba(148,163,184,0.14)',
  borderRadius: 2.5,
};

function formatAirTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString([], {
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    day: 'numeric',
    month: 'short',
  });
}

function describeContext(job) {
  const ctx = job?.context_data || {};
  const parts = [];
  if (ctx.previous_artist || ctx.previous_title) {
    parts.push(`After: ${[ctx.previous_artist, ctx.previous_title].filter(Boolean).join(' – ')}`);
  }
  if (ctx.next_artist || ctx.next_title) {
    parts.push(`Into: ${[ctx.next_artist, ctx.next_title].filter(Boolean).join(' – ')}`);
  }
  return parts.join('  ·  ');
}

/**
 * Review panel for links produced by the AI voicetrack auto-generator.
 *
 * Shows the upcoming generated links (script, guardrail warnings, status) and,
 * for links held by "require approval" mode, lets the user audition and
 * approve, skip or regenerate them before they air.
 */
export default function GeneratedLinksReview({ stationId = null }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyJobId, setBusyJobId] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [heldOnly, setHeldOnly] = useState(false);
  const [previewJobId, setPreviewJobId] = useState(null);
  const [transcriptionEnabled, setTranscriptionEnabled] = useState(true);

  const fetchJobs = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const params = { upcoming: 'true', limit: 200 };
      if (stationId) params.station_id = stationId;
      if (heldOnly) params.status = 'awaiting_approval';
      const res = await axios.get(`${API_URL}/api/ai/voices/jobs`, { params, withCredentials: true });
      setJobs(Array.isArray(res.data) ? res.data : []);
      setError('');
    } catch (err) {
      console.error('Error loading generated links:', err);
      setError(err.response?.data?.message || 'Failed to load generated links');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [stationId, heldOnly]);

  useEffect(() => {
    fetchJobs();
    const timer = setInterval(() => fetchJobs({ silent: true }), REFRESH_MS);
    return () => clearInterval(timer);
  }, [fetchJobs]);

  useEffect(() => {
    let cancelled = false;
    const params = stationId ? { station_id: stationId } : {};
    axios.get(`${API_URL}/api/ai/transcription/status`, { params, withCredentials: true })
      .then((res) => {
        if (!cancelled) setTranscriptionEnabled(res.data?.enabled !== false);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [stationId]);

  const runAction = async (job, action) => {
    setBusyJobId(job.id);
    setError('');
    setNotice('');
    try {
      const res = await axios.post(`${API_URL}/api/ai/voices/jobs/${job.id}/${action}`, {}, { withCredentials: true });
      setNotice(res.data?.message || 'Done');
      await fetchJobs({ silent: true });
    } catch (err) {
      console.error(`Error running ${action} on job ${job.id}:`, err);
      setError(err.response?.data?.message || `Failed to ${action} link`);
    } finally {
      setBusyJobId(null);
    }
  };

  const heldCount = jobs.filter((job) => job.status === 'awaiting_approval').length;

  return (
    <Card sx={cardSx}>
      <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              color: ACCENT,
              bgcolor: 'rgba(34,211,238,0.12)',
            }}
          >
            <PlaylistAddCheck />
          </Box>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--rs-text)' }}>
              Generated Links
            </Typography>
            <Typography variant="body2" sx={{ color: '#999' }}>
              Every link the auto-generator writes for the next few hours, with the script and any guardrail warnings.
              Turn on <strong>Hold AI voicetracks for approval</strong> in Settings → AI Configuration to review links
              here before they air.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControlLabel
              control={<Switch size="small" checked={heldOnly} onChange={(e) => setHeldOnly(e.target.checked)} />}
              label={<Typography variant="body2" sx={{ color: '#ccc' }}>Held only</Typography>}
            />
            <Tooltip title="Refresh">
              <IconButton onClick={() => fetchJobs()} disabled={loading} sx={{ color: ACCENT }}>
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {notice && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setNotice('')}>
            {notice}
          </Alert>
        )}
        {heldCount > 0 && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            {heldCount} link{heldCount === 1 ? '' : 's'} waiting for approval. Links not approved by air time are
            dropped and the marker plays nothing.
          </Alert>
        )}

        {loading && jobs.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} sx={{ color: ACCENT }} />
          </Box>
        ) : jobs.length === 0 ? (
          <Typography variant="body2" sx={{ color: '#999', py: 2 }}>
            No generated links coming up. Links appear here once a schedule with AI voicetrack markers is loaded.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {jobs.map((job) => {
              const meta = STATUS_META[job.status] || { label: job.status, color: '#90a4ae' };
              const warnings = Array.isArray(job.warnings) ? job.warnings : [];
              const isHeld = job.status === 'awaiting_approval';
              const canRegenerate = ['awaiting_approval', 'failed', 'expired', 'skipped', 'completed'].includes(job.status);
              const hasAudio = Boolean(job.generated_audio_path);
              const busy = busyJobId === job.id;
              const contextLine = describeContext(job);

              return (
                <Paper key={job.id} sx={{ ...jobPaperSx, borderColor: isHeld ? 'rgba(255,183,77,0.5)' : undefined }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ color: 'var(--rs-text)', fontWeight: 700 }}>
                      {formatAirTime(job.scheduled_time)}
                    </Typography>
                    <Chip
                      size="small"
                      label={meta.label}
                      sx={{ bgcolor: `${meta.color}22`, color: meta.color, fontWeight: 600 }}
                    />
                    {job.promo_name && <Chip size="small" label={`Promo: ${job.promo_name}`} sx={{ fontSize: '0.7rem' }} />}
                    {job.prompt_name && !job.promo_name && (
                      <Chip size="small" label={`Prompt: ${job.prompt_name}`} sx={{ fontSize: '0.7rem' }} />
                    )}
                    {job.voice_name && <Chip size="small" label={`Voice: ${job.voice_name}`} sx={{ fontSize: '0.7rem' }} />}
                    {job.duration ? (
                      <Chip size="small" label={`${Math.round(job.duration)}s`} sx={{ fontSize: '0.7rem' }} />
                    ) : null}
                  </Box>

                  {contextLine && (
                    <Typography variant="caption" sx={{ color: '#999', display: 'block', mb: 1 }}>
                      {contextLine}
                    </Typography>
                  )}

                  {job.generated_script ? (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" sx={{ color: '#888', fontWeight: 700, letterSpacing: 0.3 }}>
                        SCRIPT SENT TO THE VOICE
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#ddd', whiteSpace: 'pre-wrap' }}>
                        {job.generated_script}
                      </Typography>
                    </Box>
                  ) : ['failed', 'skipped', 'expired'].includes(job.status) ? (
                    <Typography variant="body2" sx={{ color: job.status === 'failed' ? '#ef9a9a' : '#bbb', mb: 1 }}>
                      {job.error_message || (job.status === 'failed' ? 'Generation failed' : 'Nothing will air at this marker')}
                    </Typography>
                  ) : (
                    <Typography variant="body2" sx={{ color: '#777', fontStyle: 'italic', mb: 1 }}>
                      Script not written yet.
                    </Typography>
                  )}

                  {warnings.length > 0 && (
                    <Alert severity="warning" sx={{ mb: 1, borderRadius: 2, py: 0.5 }}>
                      {warnings.map((warning, idx) => (
                        <Typography key={idx} variant="body2" component="div">
                          {String(warning)}
                        </Typography>
                      ))}
                    </Alert>
                  )}

                  {job.transcript && (
                    <Box
                      sx={{
                        mb: 1,
                        p: 1.25,
                        bgcolor: 'rgba(34,211,238,0.06)',
                        border: '1px solid rgba(34,211,238,0.22)',
                        borderRadius: 2,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="caption" sx={{ color: ACCENT, fontWeight: 700, letterSpacing: 0.3 }}>
                          HEARD ON AIR
                        </Typography>
                        {typeof job.transcript_match === 'number' && (
                          <Chip
                            size="small"
                            label={`${Math.round(job.transcript_match * 100)}% word match`}
                            sx={{
                              height: 18,
                              fontSize: '0.66rem',
                              bgcolor: job.transcript_match >= 0.85 ? 'rgba(76,175,80,0.16)' : 'rgba(255,183,77,0.18)',
                              color: job.transcript_match >= 0.85 ? '#4caf50' : '#ffb74d',
                            }}
                          />
                        )}
                        {job.transcript_engine && (
                          <Typography variant="caption" sx={{ color: '#777' }}>
                            {job.transcript_engine}
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="body2" sx={{ color: '#ccc', whiteSpace: 'pre-wrap', fontStyle: 'italic' }}>
                        {job.transcript}
                      </Typography>
                    </Box>
                  )}

                  {hasAudio && previewJobId === job.id && (
                    <Box sx={{ mb: 1 }}>
                      <audio
                        controls
                        autoPlay
                        src={`${API_URL}/api/ai/voices/jobs/${job.id}/audio`}
                        style={{ width: '100%', height: 36 }}
                      />
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {hasAudio && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setPreviewJobId(previewJobId === job.id ? null : job.id)}
                        sx={{ color: ACCENT, borderColor: 'rgba(34,211,238,0.4)' }}
                      >
                        {previewJobId === job.id ? 'Hide player' : 'Listen'}
                      </Button>
                    )}
                    {isHeld && (
                      <>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<CheckCircle />}
                          disabled={busy}
                          onClick={() => runAction(job, 'approve')}
                          sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#43a047' } }}
                        >
                          Approve
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<SkipNext />}
                          disabled={busy}
                          onClick={() => runAction(job, 'skip')}
                          sx={{ color: '#ccc', borderColor: 'rgba(148,163,184,0.4)' }}
                        >
                          Skip
                        </Button>
                      </>
                    )}
                    {canRegenerate && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Replay />}
                        disabled={busy}
                        onClick={() => runAction(job, 'regenerate')}
                        sx={{ color: '#ffb74d', borderColor: 'rgba(255,183,77,0.4)' }}
                      >
                        Regenerate
                      </Button>
                    )}
                    {hasAudio && job.generated_script && transcriptionEnabled && (
                      <Tooltip title="Transcribe the audio locally and compare it to the script">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<GraphicEq />}
                          disabled={busy}
                          onClick={() => runAction(job, 'transcribe')}
                          sx={{ color: '#90a4ae', borderColor: 'rgba(148,163,184,0.35)' }}
                        >
                          {job.transcript ? 'Re-check audio' : 'Check audio'}
                        </Button>
                      </Tooltip>
                    )}
                    {busy && <CircularProgress size={20} sx={{ color: ACCENT, alignSelf: 'center' }} />}
                  </Box>
                </Paper>
              );
            })}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
